import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Mail, Lock, ArrowRight, Loader2, Eye, EyeOff,
  Zap, Crown, Rocket, Star, Target, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const DEMO_USERS = [
  { email: 'alex.rivera@demo.kairera.com', password: 'demo1234', name: 'Alex Rivera', title: 'VP of Engineering', initials: 'AR', color: '#0D9488', plan: 'pro' },
  { email: 'jordan.chen@demo.kairera.com', password: 'demo1234', name: 'Jordan Chen', title: 'Product Manager', initials: 'JC', color: '#3B82F6', plan: 'free' },
  { email: 'maya.johnson@demo.kairera.com', password: 'demo1234', name: 'Maya Johnson', title: 'Chief of Staff', initials: 'MJ', color: '#805AD5', plan: 'enterprise' },
];

const PLAN_BADGES = {
  free: { bg: 'rgba(113,128,150,0.15)', color: '#64748b', label: 'Free', icon: Zap },
  pro: { bg: 'rgba(13,148,136,0.1)', color: '#0D9488', label: 'Pro', icon: Crown },
  enterprise: { bg: 'rgba(59,130,246,0.1)', color: '#3B82F6', label: 'Enterprise', icon: Rocket },
};

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [demoLoading, setDemoLoading] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.login({ email, password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      onLogin(res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (demo) => {
    setDemoLoading(demo.email);
    try {
      const res = await authAPI.login({ email: demo.email, password: demo.password });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      onLogin(res.data.user);
      navigate('/dashboard');
    } catch {
      toast.error(`Demo login failed for ${demo.name}`);
    } finally {
      setDemoLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Dark Brand */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden flex-col justify-between p-12"
        style={{ background: 'linear-gradient(135deg, #0D1E3A 0%, #0A1628 50%, #0F2847 100%)' }}>
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, rgba(13,148,136,0.4) 0%, transparent 70%)' }} />

        <div className="relative">
          <Link to="/" className="flex items-center gap-2.5 mb-16" data-testid="login-logo-link">
            <img src="/logos/Kairera-white.png" alt="Kairera" className="w-auto" style={{ height: '52px' }} />
          </Link>

          <h2 className="text-3xl font-extrabold tracking-tight leading-tight mb-4" style={{ color: '#F7FAFC' }}>
            Career decisions<br />
            backed by <span style={{ color: '#14B8A6' }}>intelligence</span>.
          </h2>
          <p className="text-sm leading-relaxed max-w-sm" style={{ color: '#7595CC' }}>
            Join thousands of professionals who've replaced guesswork with data-driven career strategy.
          </p>
        </div>

        <div className="relative space-y-4">
          <div className="p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex gap-0.5 mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" style={{ color: '#F5841F' }} />)}
            </div>
            <p className="text-[13px] leading-relaxed italic mb-3" style={{ color: '#C4D3EE' }}>
              "The career persona report was a revelation. I finally understood why certain roles energize me and others drain me."
            </p>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold"
                style={{ background: 'rgba(13,148,136,0.2)', color: '#14B8A6' }}>LK</div>
              <div>
                <p className="text-[11px] font-semibold" style={{ color: '#F7FAFC' }}>Lisa Kim</p>
                <p className="text-[9px]" style={{ color: '#4A6593' }}>Director of Strategy, Fortune 500</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            {[
              { icon: Shield, label: 'Bank-grade security' },
              { icon: Target, label: '93% satisfaction' },
            ].map(b => (
              <div key={b.label} className="flex items-center gap-1.5">
                <b.icon className="w-3 h-3" style={{ color: '#4A6593' }} />
                <span className="text-[10px] font-medium" style={{ color: '#4A6593' }}>{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - White Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10" data-testid="login-mobile-logo">
            <img src="/logos/Kairera.png" alt="Kairera" className="w-auto" style={{ height: '44px' }} />
          </Link>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-2xl font-extrabold tracking-tight mb-1" style={{ color: '#0D1E3A' }}>Welcome back</h1>
            <p className="text-sm mb-8" style={{ color: '#64748b' }}>
              Sign in to continue your career intelligence journey.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              {error && (
                <div className="px-3 py-2 rounded-lg text-xs font-medium"
                  style={{ background: 'rgba(239,68,68,0.06)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.15)' }}>
                  {error}
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold mb-1.5 block" style={{ color: '#334155' }}>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} />
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                    placeholder="you@company.com"
                    className="pl-10 h-11 text-sm rounded-lg border-slate-200 focus:border-teal-500 focus:ring-teal-500 text-slate-900"
                    data-testid="login-email-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1.5 block" style={{ color: '#334155' }}>Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} />
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                    placeholder="Enter your password"
                    className="pl-10 pr-10 h-11 text-sm rounded-lg border-slate-200 focus:border-teal-500 focus:ring-teal-500 text-slate-900"
                    data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 font-bold text-sm rounded-lg"
                style={{ background: '#0D9488', color: '#fff', boxShadow: '0 2px 12px rgba(13,148,136,0.2)' }}
                data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign In <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </form>

            <p className="text-center text-xs mt-5" style={{ color: '#94a3b8' }}>
              Don't have an account?{' '}
              <Link to="/register" className="font-semibold" style={{ color: '#0D9488' }}
                data-testid="login-register-link">Create one free</Link>
            </p>
          </motion.div>

          {/* Demo Accounts */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="mt-10 pt-8" style={{ borderTop: '1px solid #f1f5f9' }}>
            <p className="text-[9px] font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: '#94a3b8' }}>
              Quick Demo Access
            </p>
            <div className="space-y-2">
              {DEMO_USERS.map((demo, i) => {
                const badge = PLAN_BADGES[demo.plan];
                const BadgeIcon = badge.icon;
                return (
                  <motion.button key={demo.email}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 + i * 0.08 }}
                    onClick={() => handleDemoLogin(demo)}
                    disabled={demoLoading === demo.email}
                    data-testid={`demo-user-${i}`}
                    className="w-full flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.01]"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-[10px] font-black"
                      style={{ background: `${demo.color}15`, color: demo.color }}>
                      {demoLoading === demo.email ? <Loader2 className="w-4 h-4 animate-spin" /> : demo.initials}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-bold truncate" style={{ color: '#0D1E3A' }}>{demo.name}</p>
                      <p className="text-[10px] truncate" style={{ color: '#94a3b8' }}>{demo.title}</p>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold"
                      style={{ background: badge.bg, color: badge.color }}>
                      <BadgeIcon className="w-2.5 h-2.5" /> {badge.label}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
